import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { TriggerBadge } from "components/Layout/Achievement/TriggerBadge";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import React from "react";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    sectionTitle: {
      fontSize: 16,
      fontWeight: 500,
      color: theme.primary800,
      lineHeight: theme.spacing(12),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      "& > div": {
        margin: 0,
      },
    },
    sectionSubtitle: {
      color: theme.gray500,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: theme.spacing(10),
      marginTop: theme.spacing(1),
      whiteSpace: "break-spaces",
    },
    warnIcon: ({ isEditMode }: { isEditMode?: boolean }) => ({
      color: theme.orange500,
      display: isEditMode ? "inline" : "none",
    }),
    accordion: {
      boxShadow: "none",
      "& > div": {
        paddingTop: theme.spacing(2),
        minHeight: "auto",
        "&:first-child": {
          marginBottom: theme.spacing(12),
        },
        "& > div:first-child": {
          margin: 0,
          display: "block",
        },
      },
      "&.MuiAccordion-root:before": {
        display: "none",
      },
    },
    error: { color: theme.red500, marginLeft: theme.spacing(2) },
    gray: {
      color: theme.gray400,
      marginLeft: theme.spacing(2),
    },
    marginLeft: {
      marginLeft: theme.spacing(2),
    },
  }),
);

export const AlertTriangleIconWithTooltip = ({
  isEditMode,
}: {
  isEditMode: boolean;
}) => {
  const { message } = useMessage();
  const classes = useStyles({ isEditMode });

  return (
    <TooltipWithIcon
      isWhite
      placement="bottom-start"
      text={message({ id: "changingFieldWillResetProgress" })}
      icon={<AlertTriangleIcon className={classes.warnIcon} />}
    />
  );
};

interface Props {
  idx?: number;
  sectionId: string;
  title: string;
  subtitle?: string;
  details: React.ReactNode;
  isEditMode: boolean;
  isExpanded: boolean;
  hasError?: boolean;
  subtitleError?: boolean;
  hasWarning?: boolean;
  nextSectionId?: string;
  nextSectionButtonLabel?: string;
  badges?:
    | {
        icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
        label: string;
      }[]
    | undefined;
  setExpanded: React.Dispatch<React.SetStateAction<string | false>>;
}

export const AccordionSection = ({
  idx,
  sectionId,
  title,
  subtitle,
  details,
  isEditMode,
  hasWarning,
  subtitleError,
  isExpanded,
  nextSectionId,
  nextSectionButtonLabel,
  hasError = false,
  badges,
  setExpanded,
}: Props) => {
  const classes = useStyles({ isEditMode });
  const { message } = useMessage();

  const warnIcon = <AlertTriangleIconWithTooltip isEditMode={isEditMode} />;
  const errorIcon = <AlertTriangleIcon className={classes.error} />;

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, expanded: boolean) => {
      setExpanded(expanded ? panel : false);
    };

  return (
    <Accordion
      disableGutters
      expanded={isExpanded}
      onChange={handleChange(sectionId)}
      className={classes.accordion}
      data-test-id={`${sectionId}-accordion`}
    >
      <AccordionSummary expandIcon={<ChevronDownIcon />}>
        <div>
          <Typography
            className={`${classes.sectionTitle} ${hasError ? classes.error : ""}`}
          >
            {idx}.{" "}
            {message(
              { id: title },
              {
                span: (chunks: React.ReactNode[]) => (
                  <span className={classes.gray}>{chunks}</span>
                ),
              },
            )}
            {hasWarning && warnIcon}
            {hasError && errorIcon}
            {badges &&
              badges.map((badge, index) => (
                <TriggerBadge
                  Icon={badge.icon}
                  label={badge.label}
                  key={index}
                />
              ))}
          </Typography>

          {subtitle && (
            <Typography
              className={`${classes.sectionSubtitle} ${subtitleError ? classes.error : ""}`}
            >
              {message({ id: subtitle })}
            </Typography>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {details}
        {nextSectionId && (
          <Box display="flex" justifyContent="flex-end" mt={12} mb={12}>
            <OLButton
              color="primary"
              variant="contained"
              onClick={() => setExpanded(nextSectionId)}
              data-test-id={`${sectionId}-next-button`}
            >
              {message({ id: "next" })}:{" "}
              {message(
                { id: nextSectionButtonLabel },
                {
                  span: (chunks: React.ReactNode[]) => (
                    <span className={classes.marginLeft}>{chunks}</span>
                  ),
                },
              )}
            </OLButton>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
